import { VehiclesApi } from '.';

const api = new VehiclesApi();

export const getAllVehicles = (token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/vehicles`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => res.json());
  }
  return api.apiVehiclesGet().then(res => res.data);
};

export const getVehiclesByOwner = (ownerId: string, token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/vehicles?owner_id=${ownerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => res.json());
  }
  return api.apiVehiclesGet(ownerId).then(res => res.data);
};

export const createVehicle = async (vehicleData: any, token: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(vehicleData)
    });
    
    if (!response.ok) {
      throw new Error(`Erreur ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la création du véhicule:', error);
    throw error;
  }
};

export const updateVehicle = async (vehicleId: string, vehicleData: any, token: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/${vehicleId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(vehicleData)
    });
    
    if (!response.ok) {
      // Le message de l'API est celui qui dit quoi corriger — « Tarif horaire
      // hors limites », « Utilisateur introuvable », « Seul un administrateur
      // peut changer le propriétaire ». Le code est conservé en tête parce que
      // l'appelant y lit encore une session expirée (401/403).
      const detail = await response.json().catch(() => null);
      throw new Error(`Erreur ${response.status}: ${detail?.error || response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la modification du véhicule:', error);
    throw error;
  }
};

// L'identifiant est un UUID (`id_vehicle`), pas un entier : la signature en
// `number` obligeait l'appelant à un `Number(id) || (id as any)` qui ne marchait
// que parce que `Number('3f2b…')` vaut NaN et retombe sur la chaîne — et le
// deuxième appelant, lui, envoyait bel et bien /api/vehicles/NaN.
export const deleteVehicle = async (vehicleId: string, token: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/${vehicleId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const detail = await response.json().catch(() => null);
      throw new Error(`Erreur ${response.status}: ${detail?.error || response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la suppression du véhicule:', error);
    throw error;
  }
};

export const getVehicleById = async (vehicleId: string, token?: string) => {
  try {
    if (token) {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/${vehicleId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    }
    
    return api.apiVehiclesIdGet(vehicleId).then(res => res.data);
  } catch (error) {
    console.error('Erreur lors de la récupération du véhicule:', error);
    throw error;
  }
}; 
/**
 * Ajoute des photos à une annonce existante.
 *
 * `multipart/form-data`, champ `images`, six photos maximum par véhicule
 * (l'API compte celles déjà en place et refuse le lot entier si le total
 * dépasse). Volontairement sans en-tête `Content-Type` : le navigateur doit
 * poser lui-même la frontière du multipart, et la fixer à la main produit un
 * corps que le serveur ne sait pas découper.
 *
 * L'API remplace la plaque d'immatriculation par la plaque Dingo sur chaque
 * photo ; `keepPlate` demande de la garder telle quelle (honoré pour le
 * personnel seulement, ignoré sinon). Chaque image renvoyée porte
 * `plate_masked`.
 */
export const uploadVehicleImages = async (
  vehicleId: string,
  files: File[],
  token: string,
  options: { keepPlate?: boolean } = {}
) => {
  const form = new FormData();
  files.forEach((file) => form.append('images', file));
  if (options.keepPlate) form.append('keep_plate', 'true');

  const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/${vehicleId}/images`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: form
  });

  if (!response.ok) {
    // L'API explique ici des refus que l'admin peut corriger (trop de photos,
    // format non accepté) : les remplacer par un « Erreur 400 » générique
    // laisserait chercher.
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.error || `Erreur ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const deleteVehicleImage = async (vehicleId: string, imageId: string, token: string) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/${vehicleId}/images/${imageId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.error || `Erreur ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export type VehicleModelSuggestion = { brand: string; model: string };

/**
 * Modèles connus pour un type de véhicule, filtrés par un texte libre.
 *
 * `GET /api/vehicle-models?type=&q=` : cherche `q` dans la marque OU le
 * modèle, quinze lignes au plus, triées par marque puis modèle. Route
 * publique — c'est celle de l'app mobile à la publication d'une annonce, le
 * back-office ne fait que la réutiliser.
 */
export const searchVehicleModels = async (vehicleType: string, query: string): Promise<VehicleModelSuggestion[]> => {
  const params = new URLSearchParams({ type: vehicleType, q: query });
  const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicle-models?${params}`);
  if (!response.ok) return [];
  const rows = await response.json().catch(() => []);
  return Array.isArray(rows)
    ? rows.filter((r) => r && typeof r.brand === 'string' && typeof r.model === 'string')
    : [];
};
